import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { useProposal } from '../contexts/ProposalContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatCurrency } from '../utils/formatters';
import { generateProposalPDF } from '../utils/pdfGenerator';
import { FileText, Trash2, Download, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

export const ProposalBuilderPage = () => {
  const navigate = useNavigate();
  const { selectedProducts, removeFromProposal, clearProposal } = useProposal();
  const [clientName, setClientName] = React.useState('');

  const handleGeneratePDF = async () => {
    if (!clientName.trim()) {
      toast.error('Please enter client name');
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Please add products to the proposal');
      return;
    }

    try {
      console.log('Generating PDF with products:', selectedProducts);
      await generateProposalPDF(selectedProducts, clientName);
      toast.success('Proposal PDF generated successfully!');
    } catch (error) {
      console.error('PDF Generation Error:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(`Failed to generate PDF: ${error.message || 'Unknown error'}`);
    }
  };

  const totalValue = selectedProducts.reduce(
    (sum, product) => sum + product.recommended_selling_price * product.moq,
    0
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/catalogue')}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Catalogue
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-2" data-testid="proposal-title">
              Proposal Builder
            </h1>
            <p className="text-slate-600">
              {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          {selectedProducts.length > 0 && (
            <Button
              variant="outline"
              onClick={clearProposal}
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="clear-proposal-btn"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </Button>
          )}
        </div>

        {selectedProducts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="font-barlow text-xl font-semibold text-slate-900 mb-2">No products in proposal</h3>
            <p className="text-slate-600 mb-6">Add products from the catalogue to create a proposal</p>
            <Button
              onClick={() => navigate('/catalogue')}
              className="bg-consortium-green hover:bg-consortium-green-hover"
            >
              Browse Catalogue
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {selectedProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl border border-slate-200 p-6 flex gap-6"
                  data-testid={`proposal-product-${product.id}`}
                >
                  <div className="w-32 h-32 flex-shrink-0 bg-white rounded-lg border border-slate-200">
                    {product.images && product.images[0] ? (
                      <img
                        src={product.images[0].data}
                        alt={product.name}
                        className="w-full h-full object-contain rounded-lg p-1"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-2">{product.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{product.category}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-600">Price:</span>
                        <span className="font-semibold text-slate-900 ml-2">
                          {formatCurrency(product.recommended_selling_price)}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-600">MOQ:</span>
                        <span className="font-semibold text-slate-900 ml-2">{product.moq} units</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-600">Subtotal at MOQ:</span>
                        <span className="font-semibold text-consortium-green ml-2">
                          {formatCurrency(product.recommended_selling_price * product.moq)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromProposal(product.id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`remove-product-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
                <h3 className="font-barlow font-semibold text-xl text-slate-900 mb-6">Proposal Details</h3>

                <div className="mb-6">
                  <Label htmlFor="clientName">Client Name *</Label>
                  <Input
                    id="clientName"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter client/company name"
                    className="mt-2"
                    data-testid="client-name-input"
                  />
                </div>

                <div className="border-t border-slate-200 pt-4 mb-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Products:</span>
                    <span className="font-semibold text-slate-900">{selectedProducts.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Units (at MOQ):</span>
                    <span className="font-semibold text-slate-900">
                      {selectedProducts.reduce((sum, p) => sum + p.moq, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="font-semibold text-slate-900">Total Value:</span>
                    <span className="font-bold text-xl text-consortium-green">
                      {formatCurrency(totalValue)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleGeneratePDF}
                  className="w-full bg-consortium-green hover:bg-consortium-green-hover gap-2"
                  data-testid="generate-pdf-btn"
                >
                  <Download className="w-4 h-4" />
                  Generate PDF Proposal
                </Button>

                <p className="text-xs text-slate-500 mt-4 text-center">
                  PDF will include product details, images, pricing, and branding options
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
